import { z } from 'zod';
import { enrichableFieldSchema, provenanceSchema, suggestionStatusSchema } from './enums';

/** Resultado de um validador determinístico rodado após a geração. */
export const validationCheckSchema = z.object({
  id: z.string(),
  label: z.string(),
  passed: z.boolean(),
  /**
   * `blocking` reprova a sugestão antes de chegar ao revisor.
   * `warning` apenas informa o revisor.
   */
  level: z.enum(['blocking', 'warning']),
  message: z.string().nullable(),
  details: z.record(z.string(), z.unknown()).default({}),
});
export type ValidationCheck = z.infer<typeof validationCheckSchema>;

export const validationReportSchema = z.object({
  passed: z.boolean(),
  checks: z.array(validationCheckSchema),
  blockingFailures: z.array(z.string()),
  warnings: z.array(z.string()),
});
export type ValidationReport = z.infer<typeof validationReportSchema>;

export function emptyValidationReport(): ValidationReport {
  return { passed: true, checks: [], blockingFailures: [], warnings: [] };
}

export function summarizeChecks(checks: ValidationCheck[]): ValidationReport {
  const blockingFailures = checks
    .filter((c) => !c.passed && c.level === 'blocking')
    .map((c) => c.id);
  const warnings = checks.filter((c) => !c.passed && c.level === 'warning').map((c) => c.id);
  return { passed: blockingFailures.length === 0, checks, blockingFailures, warnings };
}

export const generationUsageSchema = z.object({
  model: z.string(),
  promptVersion: z.string(),
  promptTokens: z.number().int().nonnegative(),
  completionTokens: z.number().int().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
  /** USD, calculado com o preço configurado por 1M de tokens. */
  costUsd: z.number().nonnegative(),
  latencyMs: z.number().int().nonnegative(),
});
export type GenerationUsage = z.infer<typeof generationUsageSchema>;

export const suggestionSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  productId: z.number().int(),
  skuId: z.number().int().nullable(),
  fileId: z.number().int().nullable(),
  field: enrichableFieldSchema,
  currentValue: z.string().nullable(),
  suggestedValue: z.string(),
  /** Valor final após edição do revisor; `null` significa aceitar o sugerido. */
  editedValue: z.string().nullable(),
  status: suggestionStatusSchema,
  provenance: provenanceSchema,
  rationale: z.string().nullable(),
  validationReport: validationReportSchema,
  usage: generationUsageSchema.nullable(),
  snapshotContentHash: z.string(),
  createdAt: z.string(),
});
export type Suggestion = z.infer<typeof suggestionSchema>;

/** O valor que efetivamente vai para a VTEX. */
export function resolveFinalValue(
  suggestion: Pick<Suggestion, 'suggestedValue' | 'editedValue'>,
): string {
  return suggestion.editedValue ?? suggestion.suggestedValue;
}

/**
 * Proveniência final: se o revisor editou o texto da IA, o campo continua
 * sendo de origem algorítmica para efeito de rotulagem no Merchant Center,
 * mas registrado como `ai_edited`.
 */
export function resolveProvenance(editedValue: string | null): 'ai' | 'ai_edited' {
  return editedValue === null ? 'ai' : 'ai_edited';
}
